import {
  Grid,
  Box,
  Drawer,
  TextField,
  Divider,
  Button,
  Paper,
  List,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useShopFurnituresIndex } from "queries/shop_furnitures";
import { Api } from "queries/types";
import LegendItem from "components/LegendItem";
import GridCell from "components/GridCell";
import { State } from "modules/shop_grid/types";
import BaseCell from "components/BaseCell";

interface FurniturePickerProps {
  cells: State.Cell[];
  unselectAll: () => void;
  onSubmit: (configuration: {
    furniture: Api.ShopFurniture | null;
    isHighlight: boolean;
    shelvesCount: number;
  }) => void;
  close: () => void;
  brandId: number;
}

interface SelectedFurniture extends Omit<Api.ShopFurniture, "id" | "shortName"> {
  id?: number;
}

const FurniturePicker = ({ cells, unselectAll, onSubmit, close, brandId }: FurniturePickerProps) => {
  const [selectedFurniture, setSelectedFurniture] = useState<SelectedFurniture | null>(null);
  const [shelvesCount, setShelvesCount] = useState(1);
  const [isHighlight, setIsHighlight] = useState(false);
  const [search, setSearch] = useState("");
  const { t } = useTranslation();
  const { data: allFurnitures = [], isLoading } = useShopFurnituresIndex(brandId);
  const furnitureList = (allFurnitures as SelectedFurniture[]).concat([
    { name: t("empty"), color: "#767676", isRecordable: false },
  ]);
  const filteredFurnitures =
    search === ""
      ? furnitureList
      : furnitureList.filter((furniture) => furniture.name.toLowerCase().match(search.toLowerCase()));

  return (
    <Drawer anchor="right" open onClose={close} variant="persistent">
      <Box sx={{ width: "400px", height: "100%", maxHeight: "100vh" }}>
        <Grid container direction="column" height="100%">
          <Grid item maxHeight="25vh" overflow="auto">
            <Grid container>
              <Grid item sm={12} sx={{ px: 4, py: 2 }}>
                <Box sx={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                  <Typography>
                    {t("selectedCells", { count: cells.length })}{" "}
                    <Button size="small" color="warning" onClick={unselectAll}>
                      {t("unselect")}
                    </Button>
                  </Typography>
                </Box>
                {cells.map((cell: State.Cell) => (
                  <LegendItem
                    key={`${cell.yAxis}-${cell.xAxis}`}
                    text={`(${cell.yAxis};${cell.xAxis}) ${cell.furniture?.name || t("empty")}`}
                  >
                    <GridCell mode="furniture" cell={cell} size="small" />
                  </LegendItem>
                ))}
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <Grid item maxHeight="60vh" overflow="auto">
            {selectedFurniture ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    background: selectedFurniture.color,
                    p: 4,
                  }}
                >
                  <Paper sx={{ p: 1, width: "100%" }}>
                    <Typography variant="h5">{selectedFurniture.name}</Typography>
                    <Button
                      size="small"
                      sx={{ float: "right" }}
                      onClick={() => {
                        setSelectedFurniture(null);
                        setShelvesCount(1);
                        setIsHighlight(false);
                      }}
                    >
                      {t("edit")}
                    </Button>
                  </Paper>
                </Box>
                {selectedFurniture.isRecordable && (
                  <>
                    <Divider />
                    <Typography variant="h6" sx={{ px: 4, mt: 2 }}>
                      {t("furnitureSpecificities")}
                    </Typography>
                    <Grid container px={4} my={2}>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel>{t("shop.edit.shelvesCount")}</InputLabel>
                          <Select
                            value={shelvesCount}
                            fullWidth
                            label={t("shop.edit.shelvesCount")}
                            onChange={(e) => setShelvesCount(e.target.value as number)}
                          >
                            <MenuItem value={1}>{t("shop.edit.shelvesCount1")}</MenuItem>
                            <MenuItem value={2}>{t("shop.edit.shelvesCount2")}</MenuItem>
                            <MenuItem value={3}>{t("shop.edit.shelvesCount3")}</MenuItem>
                            <MenuItem value={4}>{t("shop.edit.shelvesCount4")}</MenuItem>
                            <MenuItem value={5}>{t("shop.edit.shelvesCount5")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={3} ml={4}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox checked={isHighlight} onChange={(e) => setIsHighlight(e.target.checked)} />
                            }
                            label={t("shop.edit.isHighlight")}
                          />
                        </FormGroup>
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            ) : (
              <>
                <Box sx={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                  <Grid sx={{ fontWeight: "400", fontSize: "18px" }} item p={4} py={2}>
                    {t("shop.edit.furnitureChoice")}
                  </Grid>
                  <Grid px={4} item>
                    <TextField
                      label={t("search")}
                      type="search"
                      size="small"
                      fullWidth
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Grid>
                </Box>
                <Grid sx={{ fontWeight: "400", fontSize: "18px", overflow: "auto" }} item pl={4}>
                  {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", height: "60px" }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <List sx={{ overflow: "auto" }}>
                      {filteredFurnitures.map((furniture) => (
                        <LegendItem
                          key={"id" in furniture ? furniture.id : "empty"}
                          onClick={() => setSelectedFurniture(furniture)}
                          text={furniture.name}
                          sx={{ cursor: "pointer" }}
                        >
                          <BaseCell color={furniture.color} size="small" />
                        </LegendItem>
                      ))}
                    </List>
                  )}
                </Grid>
              </>
            )}
          </Grid>
          <Divider />
          <Grid item px={4} pt={2} mt="auto" mb={2} justifySelf="flex-end">
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                onSubmit({
                  furniture: "id" in selectedFurniture! ? (selectedFurniture as Api.ShopFurniture) : null,
                  isHighlight,
                  shelvesCount,
                });
                close();
              }}
              sx={{ mb: 2 }}
              disabled={!selectedFurniture}
            >
              {t("validate")}
            </Button>
            <Button fullWidth onClick={close}>
              {t("cancel")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
};

export default FurniturePicker;
